import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Sheet, Database, FileText, Cloud, Zap } from "lucide-react";
import UploadHeader from "./components/UploadHeader";
import UploadSection from "./components/UploadSection";
import DownloadSection from "./components/DownloadSection";
import { useAuth } from "../../../context/AuthContext";
import { FILE_UPLOAD } from "../../../constants";
import backendApi from "../../../services/backendApi";
import { buildExcelUploadStorageKey } from "../forms/sites/Axon/utils/storageKeys";

interface DataResponse {
  ambient?: any[];
  bd?: any[];
  databank?: any[];
  master?: any[];
  meter?: any[];
}

interface UploadResponse {
  message: string;
  data?: DataResponse;
  errors?: Record<string, unknown>;
  site: string;
}

const DGRUploadPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const { user } = useAuth();

  const [file, setFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [processingProgress, setProcessingProgress] = useState<number>(0);
  const [processingStatusText, setProcessingStatusText] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const [ambientData, setAmbientData] = useState<any[]>([]);
  const [bdData, setBdData] = useState<any[]>([]);
  const [databankData, setDatabankData] = useState<any[]>([]);
  const [masterData, setMasterData] = useState<any[]>([]);
  const [meterData, setMeterData] = useState<any[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>(() => {
    try {
      const stored = localStorage.getItem('dgr_upload_selected_sheet');
      const validSheets = ["master", "databank", "bd", "ambient", "meter", "module_cleaning", "grass_cutting", "daily_checklist", "weekly_checklist", "monthly_checklist", "quarterly_checklist", "yearly_checklist"];
      if (stored && validSheets.includes(stored)) {
        return stored;
      }
    } catch (e) {}
    return "";
  });

  useEffect(() => {
    if (selectedSheet) {
      try {
        localStorage.setItem('dgr_upload_selected_sheet', selectedSheet);
      } catch (e) {}
    }
  }, [selectedSheet]);

  const [siteName, setSiteName] = useState<string>("");
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [totalCounts, setTotalCounts] = useState<Record<string, number>>({});
  const [userCounts, setUserCounts] = useState<Record<string, number>>({});

  // API base URL - normalize for production
  const getApiBase = (): string => {
    const raw = (window as any).__API_BASE__ || `${window.location.protocol}//${window.location.hostname}:8000`;
    return String(raw).replace(/:\/\/localhost/, `://${window.location.hostname}`);
  };
  const API_BASE = getApiBase() + '/api/v1';

  /**
   * Normalize date strings to YYYY-MM-DD where possible.
   * Falls back to trimmed input when parsing fails.
   */
  const normalizeDate = (value: any): string => {
    if (!value) return '';
    const str = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const parsed = new Date(str);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }
    return str;
  };

  /**
   * Map backend BD row to the structure expected by the BD entry sheet.
   * Keeps original strings so users see exactly what was uploaded.
   */
  const mapBdRowForEntry = (row: any) => {
    const date = normalizeDate(row?.entry_date ?? row?.date ?? row?.Date ?? '');
    return {
      date,
      'Budget PR for the month': row?.budget_pr_for_month ?? row?.['Budget PR for the month'] ?? '',
      'Equipment': row?.equipment ?? row?.Equipment ?? '',
      'BD Category': row?.bd_category ?? row?.['BD Category'] ?? '',
      'ICR No': row?.icr_no ?? row?.['ICR No'] ?? '',
      'Inverter No.': row?.inverter_no ?? row?.['Inverter No.'] ?? '',
      'BD From': row?.bd_from ?? row?.['BD From'] ?? '',
      'BD up  to': row?.bd_up_to ?? row?.['BD up  to'] ?? '',
      'BD Minutes': row?.bd_minutes ?? row?.['BD Minutes'] ?? '',
      'BD Hours': row?.bd_hours ?? row?.['BD Hours'] ?? '',
      'PA BD Hours': row?.pa_bd_hours ?? row?.['PA BD Hours'] ?? '',
      'A = DC Capacity of affected equipment (kW)': row?.dc_capacity_kw ?? row?.['A = DC Capacity of affected equipment (kW)'] ?? '',
      'B = POA lost during BD (kWh/m2)': row?.poa_lost_kwh_per_m2 ?? row?.['B = POA lost during BD (kWh/m2)'] ?? '',
      'C=A*B': row?.c_a_times_b ?? row?.['C=A*B'] ?? '',
      'Generation loss kWh': row?.generation_loss_kwh ?? row?.['Generation loss kWh'] ?? '',
      "SCB's /QS/UNIT": row?.scb_qs_unit ?? row?.["SCB's /QS/UNIT"] ?? '',
      'BD Description': row?.bd_description ?? row?.['BD Description'] ?? '',
      'Corrective Action Taken': row?.corrective_action_taken ?? row?.['Corrective Action Taken'] ?? '',
      'Current Status': row?.current_status ?? row?.['Current Status'] ?? '',
      'Effective loss of POA': row?.effective_loss_of_poa ?? row?.['Effective loss of POA'] ?? '',
      'Scope': row?.scope ?? row?.['Scope'] ?? '',
      'Which Equipment is in off condition': row?.equipment_off_condition ?? row?.['Which Equipment is in off condition'] ?? '',
      'Cause (Error Code)': row?.cause_error_code ?? row?.['Cause (Error Code)'] ?? ''
    };
  };

  /**
   * Cache uploaded data for the entry pages (localStorage).
   * Specifically handles BD Details so repeated dates are preserved and ordered.
   */
  const cacheUploadForEntry = (siteCode: string, data: DataResponse) => {
    if (!siteCode) return;
    const storageKey = buildExcelUploadStorageKey(siteCode, user);
    const legacyStorageKey = `excelUploadData_${siteCode}`;

    // Only handle BD Details per request
    const bdRows = Array.isArray(data?.bd) ? data.bd : [];
    if (bdRows.length === 0) {
      localStorage.removeItem(storageKey);
      localStorage.removeItem(legacyStorageKey);
      return;
    }

    // Map rows and sort by date then by id (newest first within a date)
    const mappedBd = bdRows
      .map((row) => ({
        ...mapBdRowForEntry(row),
        _id: row?.id ?? row?.pk ?? 0 // keep original id for stable ordering
      }))
      .sort((a, b) => {
        const dateA = normalizeDate(a.date);
        const dateB = normalizeDate(b.date);
        if (dateA !== dateB) return dateA.localeCompare(dateB);
        return (b._id || 0) - (a._id || 0);
      })
      .map(({ _id, ...rest }) => rest); // drop helper id

    const excelPayload: Record<string, any> = {
      'BD Details': mappedBd
    };

    try {
      localStorage.setItem(storageKey, JSON.stringify(excelPayload));
      localStorage.removeItem(legacyStorageKey);
} catch (err) {
}
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();
      if (fileExtension && FILE_UPLOAD.ALLOWED_TYPES.includes(fileExtension)) {
        if (selectedFile.size > FILE_UPLOAD.MAX_SIZE) {
          setUploadStatus(`error|File size exceeds ${FILE_UPLOAD.MAX_SIZE / 1024 / 1024}MB limit`);
          // Reset input on validation failure
          e.target.value = '';
          return;
        }
        setFile(selectedFile);
        setUploadStatus("");
        setUploadProgress(0);
        setProcessingProgress(0);
      } else {
        setUploadStatus(`error|Please select a valid file (${FILE_UPLOAD.ALLOWED_TYPES.join(', ').toUpperCase()})`);
        // Reset input on validation failure
        e.target.value = '';
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      const droppedFile = droppedFiles[0];
      const fileExtension = droppedFile.name.split('.').pop()?.toLowerCase();
      if (fileExtension && FILE_UPLOAD.ALLOWED_TYPES.includes(fileExtension)) {
        if (droppedFile.size > FILE_UPLOAD.MAX_SIZE) {
          setUploadStatus(`error|File size exceeds ${FILE_UPLOAD.MAX_SIZE / 1024 / 1024}MB limit`);
          return;
        }
        setFile(droppedFile);
        setUploadStatus("");
        setUploadProgress(0);
        setProcessingProgress(0);
        // Also reset the file input to keep them in sync
        const fileInput = document.getElementById('file-upload') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      } else {
        setUploadStatus(`error|Please select a valid file (${FILE_UPLOAD.ALLOWED_TYPES.join(', ').toUpperCase()})`);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleUpload = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (isUploading) return;
    if (!file || !site) {
      setUploadStatus("error|Please select a file to upload");
      return;
    }

    const fileToUpload = file;

    if (!fileToUpload || fileToUpload.size === 0) {
      setUploadStatus("error|File is invalid or has been removed. Please select a file again.");
      setFile(null);
      return;
    }

    setIsUploading(true);
    setIsProcessing(false);
    setUploadStatus("");
    setUploadProgress(0);
    setProcessingProgress(0);
    setProcessingStatusText("Preparing upload...");

    const token = sessionStorage.getItem('access_token');
    if (!token) {
      setUploadStatus("error|Authentication token not found. Please log in again.");
      setIsUploading(false);
      return;
    }

    if (!selectedSheet) {
      setUploadStatus("error|Please select a sheet name before uploading");
      setIsUploading(false);
      return;
    }

    // Generate a unique upload ID
    const uploadId = `upl_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const formData = new FormData();
    formData.append("file", fileToUpload, fileToUpload.name);
    formData.append("sheet_name", selectedSheet);
    formData.append("upload_id", uploadId);
    formData.append("response_mode", "summary");

    let pollInterval: NodeJS.Timeout | null = null;

    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const startPolling = () => {
      if (pollInterval) return;

      const poll = async () => {
        try {
          const res = await fetch(`${API_BASE}/upload-progress/${uploadId}/`);
          if (res.ok) {
            const pData = await res.json();
            if (pData.percent !== undefined && pData.percent >= 0) {
              setProcessingProgress(pData.percent);
            }
            if (pData.status) {
              setProcessingStatusText(pData.status);
            }

            if (pData.status === 'complete') {
              stopPolling();
              setProcessingProgress(100);
              setUploadStatus(`success|${pData.message || 'Upload complete!'}`);
              await fetchData();
              setFile(null);
              setUploadProgress(0);
              setProcessingProgress(0);
              setProcessingStatusText("");
              setIsUploading(false);
              setIsProcessing(false);

              const fileInput = document.getElementById('file-upload') as HTMLInputElement;
              if (fileInput) {
                fileInput.value = '';
              }
            } else if (pData.status === 'error') {
              stopPolling();
              setUploadStatus(`error|${pData.message || 'Processing failed'}`);
              setIsUploading(false);
              setIsProcessing(false);
            }
          }
        } catch (e) {
          console.error("Polling error", e);
        }
      };

      poll();
      pollInterval = setInterval(poll, 150);
    };

    try {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);

          if (percentComplete === 100) {
            setIsProcessing(true);
            setProcessingStatusText("File uploaded. Server processing started in background...");
            startPolling();
          }
        }
      });

      const promise = new Promise<void>((resolve, reject) => {
        xhr.addEventListener("load", async () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            startPolling();
            resolve();
          } else {
            stopPolling();
            let errorMessage = `Upload failed: ${xhr.status} ${xhr.statusText}`;
            try {
              const errorJson = JSON.parse(xhr.responseText);
              if (errorJson.message) {
                errorMessage = errorJson.message;
              } else if (errorJson.error) {
                errorMessage = errorJson.error;
              }
            } catch {
              if (xhr.responseText) {
                errorMessage = xhr.responseText.substring(0, 200);
              }
            }
            reject(new Error(errorMessage));
          }
        });

        xhr.addEventListener("error", () => {
          stopPolling();
          reject(new Error("Network error during upload"));
        });

        xhr.addEventListener("abort", () => {
          stopPolling();
          reject(new Error("Upload aborted"));
        });
      });

      xhr.open("POST", `${API_BASE}/upload-dgr/${site}/`);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.send(formData);

      await promise;

    } catch (err: any) {
      stopPolling();
      const errorMessage = err?.message || "Unknown error occurred";
      console.error("Upload error:", err);
      setUploadStatus(`error|Upload failed: ${errorMessage}`);
      setIsUploading(false);
      setIsProcessing(false);
    }
  };

  const fetchData = async (): Promise<void> => {
    if (!site) return;
    setIsRefreshing(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE}/get-dgr/${site}/?summary_only=1`, {
        headers
      });

      if (!response.ok) {
        // If 404, endpoint might not exist - set empty data
        if (response.status === 404) {
setAmbientData([]);
          setBdData([]);
          setDatabankData([]);
          setMasterData([]);
          setMeterData([]);
          setUserCounts({});
          return;
        }

        const errorText = await response.text();
        console.error("Failed to fetch DGR data:", response.status, errorText);
        return;
      }

      // Check if response is JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        console.error("Response is not JSON:", contentType);
        // Try to read as text to see what we got
        const text = await response.text();
        console.error("Response text:", text.substring(0, 200));
        return;
      }

      const payload = await response.json();
// In summary mode we avoid loading large row payloads to keep the UI responsive.
      setAmbientData([]);
      setBdData([]);
      setDatabankData([]);
      setMasterData([]);
      setMeterData([]);

      setCounts(payload?.counts || {});
      setTotalCounts(payload?.total_counts || {});
      setUserCounts(payload?.user_counts || {});
    } catch (err: any) {
      // Handle network errors, timeouts, etc.
      const errorMessage = err?.message || "Unknown error occurred";
      console.error("Failed to fetch parsed DGR data:", errorMessage, err);
      // Don't throw - just log and continue with empty data
      setAmbientData([]);
      setBdData([]);
      setDatabankData([]);
      setMasterData([]);
      setMeterData([]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const downloadSheet = async (type: string, label: string): Promise<void> => {
    if (!user) {
      alert("Please log in to download data");
      return;
    }

    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        alert("Authentication token not found. Please log in again.");
        return;
      }

      const siteParam = site ? `?site_code=${encodeURIComponent(site.toUpperCase())}` : '';
      const response = await fetch(`${API_BASE}/download-sheet/${type}/${siteParam}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(site ? { 'X-Site-Code': site.toUpperCase() } : {}),
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          alert("Authentication failed. Please log in again.");
          return;
        }
        const errorText = await response.text();
        console.error("Download failed:", response.status, response.statusText, errorText);
        alert(`Download failed: ${response.status} ${response.statusText}`);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const sheetLabel = label || type;
      const siteCode = site?.toUpperCase() || 'SITE';
      const userLabel = user.username || 'user';
      a.download = `${sheetLabel} - ${siteCode} - ${userLabel}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      const errorMessage = error?.message || "Unknown error occurred";
      console.error("Download failed:", errorMessage, error);
      alert(`Download failed: ${errorMessage}`);
    }
  };

  const downloadAll = async (): Promise<void> => {
    if (!user) {
      alert("Please log in to download data");
      return;
    }

    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        alert("Authentication token not found. Please log in again.");
        return;
      }

      const siteParam = site ? `?site_code=${encodeURIComponent(site.toUpperCase())}` : '';
      const response = await fetch(`${API_BASE}/download-all/${siteParam}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(site ? { 'X-Site-Code': site.toUpperCase() } : {}),
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          alert("Authentication failed. Please log in again.");
          return;
        }
        const errorText = await response.text();
        console.error("Download all failed:", response.status, response.statusText, errorText);
        alert(`Download failed: ${response.status} ${response.statusText}`);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const siteCode = site?.toUpperCase() || 'SITE';
      const userLabel = user.username || 'user';
      a.download = `Generation Reports - ${siteCode} - ${userLabel}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      const errorMessage = error?.message || "Unknown error occurred";
      console.error("Download all failed:", errorMessage, error);
      alert(`Download failed: ${errorMessage}`);
    }
  };

  // Get user-specific counts for each sheet type
  const getCountForType = (type: string): number => {
    // Map frontend type names to backend keys
    const typeMap: Record<string, string> = {
      "master": "master",
      "databank": "databank",
      "bd": "bd",
      "ambient": "ambient",
      "meter": "meter",
      "module_cleaning": "module_cleaning",
      "grass_cutting": "grass_cutting",
      "daily_checklist": "daily_checklist",
      "weekly_checklist": "weekly_checklist",
      "monthly_checklist": "monthly_checklist",
      "quarterly_checklist": "quarterly_checklist",
      "yearly_checklist": "yearly_checklist"
    };
    const backendKey = typeMap[type] || type;

    const role = (user?.role || '').toLowerCase().replace(' ', '_');
    const isAdmin = user?.isGlobalAdmin || role.includes('admin') || role === 'ho';

    const source = isAdmin ? totalCounts : userCounts;

    // Try exact match first
    let count = source[backendKey];

    // If not found, try case-insensitive match
    if (count === undefined) {
      const lowerKey = backendKey.toLowerCase();
      const foundKey = Object.keys(source).find(k => k.toLowerCase() === lowerKey);
      if (foundKey) {
        count = source[foundKey];
      }
    }

    // Default to 0 if still not found
    if (count === undefined) {
      count = 0;
    }

    return count;
  };

  // Memoize download options to avoid recalculating on every render
  const downloadOptions = useMemo(() => {
    const options = [
      // Generation Reports Sheets
      { type: "master", label: "Daily Report", icon: <Sheet size={18} />, count: getCountForType("master"), category: "Generation Reports" },
      { type: "databank", label: "Inverters", icon: <Database size={18} />, count: getCountForType("databank"), category: "Generation Reports" },
      { type: "bd", label: "Breakdowns", icon: <FileText size={18} />, count: getCountForType("bd"), category: "Generation Reports" },
      { type: "ambient", label: "Weather", icon: <Cloud size={18} />, count: getCountForType("ambient"), category: "Generation Reports" },
      { type: "meter", label: "Plant Meters", icon: <Zap size={18} />, count: getCountForType("meter"), category: "Generation Reports" },
      // O&M(Checklist) Sheets
      { type: "module_cleaning", label: "Module Cleaning", icon: <FileText size={18} />, count: getCountForType("module_cleaning"), category: "O&M (Checklist)" },
      { type: "grass_cutting", label: "Grass Cutting", icon: <FileText size={18} />, count: getCountForType("grass_cutting"), category: "O&M (Checklist)" },
      { type: "daily_checklist", label: "Daily Checklist", icon: <FileText size={18} />, count: getCountForType("daily_checklist"), category: "O&M (Checklist)" },
      { type: "weekly_checklist", label: "Weekly Checklist", icon: <FileText size={18} />, count: getCountForType("weekly_checklist"), category: "O&M (Checklist)" },
      { type: "monthly_checklist", label: "Monthly Checklist", icon: <FileText size={18} />, count: getCountForType("monthly_checklist"), category: "O&M (Checklist)" },
      { type: "quarterly_checklist", label: "Quarterly Checklist", icon: <FileText size={18} />, count: getCountForType("quarterly_checklist"), category: "O&M (Checklist)" },
      { type: "yearly_checklist", label: "Yearly Checklist", icon: <FileText size={18} />, count: getCountForType("yearly_checklist"), category: "O&M (Checklist)" }
    ];
return options;
  }, [userCounts, totalCounts, user]);

  // Fetch site name from database
  useEffect(() => {
    const fetchSiteName = async () => {
      if (!site) return;
      try {
        const response = await backendApi.getSites(false, site.toUpperCase());
        if (response.success && Array.isArray(response.sites)) {
          const matchedSite = response.sites.find(
            (item: any) => item.site_code && item.site_code.toUpperCase() === site.toUpperCase()
          ) || response.sites[0];

          if (matchedSite && matchedSite.site_name) {
            setSiteName(matchedSite.site_name);
          } else {
            setSiteName(site.toUpperCase());
          }
        } else {
          setSiteName(site.toUpperCase());
        }
      } catch (error) {
setSiteName(site.toUpperCase());
      }
    };

    fetchSiteName();
  }, [site]);

  useEffect(() => {
    fetchData();
  }, [site, user]);

  if (!site) {
    return <div className="pt-16 p-6 text-center text-red-600">Error: Site parameter is missing</div>;
  }

  return (
    <div className="upload-page bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col" style={{ height: '100vh', paddingTop: '4rem' }}>
      {/* Modern Header */}
      <div className="flex-shrink-0">
        <UploadHeader
          siteName={siteName || site?.toUpperCase() || ""}
          onRefresh={fetchData}
          isRefreshing={isRefreshing}
        />
      </div>

      {/* Main Content - Modern Card Layout - Full Width */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="w-full h-full px-6 py-4">
          {/* Two Column Layout - Wider with Less Gap */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">

            {/* Upload Section - Modern Design */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden flex flex-col h-full">
              <div className="bg-gradient-to-r from-[#0084CF] to-[#006ba3] px-4 py-3 flex items-center gap-2 flex-shrink-0">
                <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm">
                  <FileText size={18} className="text-white" />
                </div>
                <h2 className="text-lg text-white" style={{ fontWeight: 400 }}>Upload File</h2>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-4">
                <UploadSection
                  file={file}
                  isDragOver={isDragOver}
                  isUploading={isUploading}
                  uploadStatus={uploadStatus}
                  selectedSheet={selectedSheet}
                  uploadProgress={uploadProgress}
                  processingProgress={processingProgress}
                  processingStatusText={processingStatusText}
                  isProcessing={isProcessing}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onFileChange={handleFileChange}
                  onUpload={handleUpload}
                  onSheetChange={setSelectedSheet}
                />
              </div>
            </div>

            {/* Download Section - Modern Design */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden flex flex-col h-full">
              <div className="bg-gradient-to-r from-[#69951A] to-[#577a15] px-4 py-3 flex items-center gap-2 flex-shrink-0">
                <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm">
                  <Cloud size={18} className="text-white" />
                </div>
                <h2 className="text-lg text-white" style={{ fontWeight: 400 }}>Download File</h2>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-4">
                <DownloadSection
                  downloadOptions={downloadOptions}
                  onDownloadSheet={downloadSheet}
                  onDownloadAll={downloadAll}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DGRUploadPage;



